import {
  type FocusEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  CloseOutlined,
  DownOutlined,
  IconShipTogether,
  SearchOutlined,
  UploadOutlined,
} from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { EmptyState } from '../../components/ui/EmptyState'
import { Skeleton } from '../../components/ui/Skeleton'
import { Tabs } from '../../components/ui/Tabs'
import { useToast } from '../../components/ui/ToastProvider'
import { useCartCount } from '../../lib/cart/cart-count-context'
import { useLoadMoreSentinel } from '../../lib/list/useLoadMoreSentinel'
import { useRfqBundle } from '../../lib/rfq/rfq-bundle-context'
import { toSafeImageRef } from '../../lib/thumbnail-image-ref'
import { useDebouncedValue } from '../uploads/use-debounced-value'
import type { WorkspaceSummary } from '../workspaces/types'
import { useWorkspaceList } from '../workspaces/useWorkspaces'
import { MAX_BULK_CONFIGURE_PARTS, bulkConfigureParts } from './bulk-configure-service'
import { AddToProjectMenu } from './components/AddToProjectMenu'
import { BomCardGrid } from './components/BomCardGrid'
import { BulkActionBar } from './components/BulkActionBar'
import { CreateRevisionModal } from './components/CreateRevisionModal'
import { EditConfigurationModal } from './components/EditConfigurationModal'
import { PartShareModal } from './components/PartShareModal'
import { PartsBulkConfigureModal } from './components/PartsBulkConfigureModal'
import { PartsTable } from './components/PartsTable'
import { StatusFilterBar } from './components/StatusFilterBar'
import type { EditPartConfigRequest } from './edit-part-config-service'
import {
  allPriced,
  anyNeedsConfig,
  anyNeedsQuote,
  selectNeedsConfig,
  selectNeedsQuote,
} from './row-status'
import type { LibraryPartRow, PartDetail, PartStatusFilter, PartsSortKey } from './types'
import { useBoms } from './useBoms'
import type { BomsService } from './useBoms'
import { defaultPartsLibraryService, usePartsLibrary } from './usePartsLibrary'
import type { PartsLibraryService } from './usePartsLibrary'

export interface PartsLibraryPageProps {
  /** Injectable for tests / future BFF-backed swap; defaults to the mock
   * (see `usePartsLibrary.ts`'s doc comment on the `PartsLibraryService` seam). */
  readonly service?: PartsLibraryService
  /** UID-290 — injectable seam for the BOMs tab's OWN `GET /boms` fetch
   * (separate from `service.listLibrary` above); defaults to the real
   * BFF-backed `defaultBomsService` (see `useBoms.ts`). */
  readonly bomsService?: BomsService
}

const SKELETON_ROW_KEYS = ['a', 'b', 'c', 'd', 'e']

type TabId = 'parts' | 'boms'

/**
 * Parts Library (UID-44, screens.md §5) — "everything uploaded or
 * purchased," tabs for parts vs. BOMs. Presentational-first against
 * `usePartsLibrary`'s mock-backed `PartsLibraryService` (see that file's
 * doc comment) — every data region here reads from `state`, and the
 * search/status/sort/group-by-session controls drive the SAME query-param
 * shape the real `GET /api/parts` call will take, so swapping the mock for
 * a BFF-backed service is the only change needed once UID-42/CC listing
 * endpoints exist.
 */
export function PartsLibraryPage({
  service = defaultPartsLibraryService,
  bomsService,
}: PartsLibraryPageProps = {}) {
  const [tab, setTab] = useState<TabId>('parts')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<PartStatusFilter>('all')
  const [sortKey, setSortKey] = useState<PartsSortKey>('recent')
  const [groupBySession, setGroupBySession] = useState(false)
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set())
  // UID-470 — the row checkbox last given a PLAIN click/keyboard toggle (not
  // a shift-click), i.e. the Gmail/Finder "range anchor" a subsequent
  // shift-click ranges FROM. See `toggleSelect` below for the full anchor
  // semantics, including how/when it resets.
  const [selectionAnchorId, setSelectionAnchorId] = useState<string | null>(null)
  const [reorderingId, setReorderingId] = useState<string | null>(null)
  const [configPart, setConfigPart] = useState<LibraryPartRow | null>(null)
  // UID-422 — "Share part" (⋮ row menu). Mounted only while a row is being
  // shared, matching `configPart`'s sibling convention just above, so every
  // open starts `PartShareModal` fresh (its own search/added/copied state
  // resets by remounting rather than by an explicit reset effect).
  const [sharePart, setSharePart] = useState<LibraryPartRow | null>(null)
  // UID-422 — "Remove from library" (⋮ row menu). Mirrors `WorkspacePage.tsx`'s
  // "Delete project" `ConfirmDialog` posture: the target row gates the dialog
  // open/closed (mounted only while confirming, same convention as `configPart`/
  // `sharePart` above), `removeBusy` guards a fast double-click on the dialog's
  // own confirm button, and `removeError` surfaces INSIDE the still-open dialog
  // (via its `error` prop) rather than a toast, so the shopper sees why the
  // removal failed without the dialog closing out from under them.
  const [removeTarget, setRemoveTarget] = useState<LibraryPartRow | null>(null)
  const [removeBusy, setRemoveBusy] = useState(false)
  const [removeError, setRemoveError] = useState<string | undefined>(undefined)
  const [bulkCartBusy, setBulkCartBusy] = useState(false)
  // Gate 5 code review (S6) — WCAG 4.1.3: `handleAddToRfq`'s
  // `setSelectedIds(new Set())` unmounts the `BulkActionBar` containing the
  // button the user just pressed, dropping focus to `document.body` with
  // nothing announced (under the old immediate-navigate model, `navigate()`
  // covered this). `bundleAnnouncement` feeds a polite live region (same
  // sr-only pattern as `RfqListPage.tsx`'s `liveMessage`/`draftAnnouncement`)
  // so the state change is still announced now that the visible "RFQ bundle"
  // banner has been removed.
  const [bundleAnnouncement, setBundleAnnouncement] = useState('')
  // UID-257 — "Configure N" opens the bulk-configuration workflow for every
  // selected part (design handoff §8). Snapshots the ROWS (not just ids) at
  // open time so the applied batch — and the modal's mixed-value advisory /
  // price preview, which both read row data (UID-46, AC2/AC3) — are stable
  // even if the selection or listing changes underneath while it's open.
  const [bulkConfigureRows, setBulkConfigureRows] = useState<readonly LibraryPartRow[] | null>(null)
  // UID-46 — "Apply to multiple parts" (the row-level `EditConfigurationModal`
  // button) applies ITS current form state to the active multi-selection via
  // the same loop-PATCH bulk service. Own busy flag, independent of that
  // modal's single-part `saving` state (same per-action-busy rationale as
  // `BulkActionBar`).
  const [applyToMultipleBusy, setApplyToMultipleBusy] = useState(false)
  // UID-376 — "Create revision" (design's ⋮ row menu, exactly Share part /
  // Create revision / Remove from library). The row only carries a
  // `LibraryPartRow` (listing shape); `CreateRevisionModal` needs a full
  // `PartDetail` — the SAME shape `PartDetailPage` already has in scope — so
  // opening it here fetches on demand via the existing
  // `service.getPartDetail` seam (already used by `usePartDetail`) rather
  // than inventing a new endpoint.
  const [revisionPart, setRevisionPart] = useState<PartDetail | null>(null)
  // UID-297 — the toolbar's "All projects" filter narrows the ALREADY-FETCHED
  // `allParts` client-side against each row's server-hydrated `projects`
  // (durable — survives a refresh, and includes parts linked in a PRIOR
  // session, unlike the old FE-only accumulated map this replaced).
  // Deliberately NOT a `?workspaceId=` server refetch here: `allParts` must
  // stay the FULL unfiltered set so bulk-action gating/queueing
  // (`selectedRows` below) keeps operating on every selected row regardless
  // of which project the toolbar is currently showing (UID-284) — narrowing
  // the FETCH itself would silently lose a selected-but-filtered-out row the
  // same way UID-284 did. `?workspaceId=` is real and tested server-side
  // (UID-297); Slice C's workspace-scoped tabs are its consumer. Note this
  // means `counts.parts` (the "My parts" tab badge, below) stays the FULL
  // library total while this filter is active, exactly like the existing
  // search/status filters — only the visible `parts` narrows.
  const [projectFilter, setProjectFilter] = useState<string | null>(null)
  // UID-183/UID-377 — backs the toolbar's "All projects" filter menu
  // (`projectOptions` below). This fetch used to have a second consumer, the
  // Library page's "Projects" tab; UID-377 removed that tab, because
  // `Fictiv Platform.dc.html`'s Library tab strip (L1267-1270) renders exactly
  // two tabs and nothing anywhere in the comp clicks a third into view.
  //
  // Read this before "restoring" the tab: the comp DOES carry a
  // `libShowProjects` project-grid panel in live Library markup (L1561) — that
  // is not an argument for the tab. Nothing selects it: `goLibraryProjects`
  // (L10458) and `libTabProjects` (L10446) are defined in the state mapper and
  // referenced by NO markup, and the one path that sets `libTab:'projects'`
  // (L7327, after creating a project) never calls `this.go('library')`, so it
  // cannot navigate you there — it lands in a state where neither tab span is
  // highlighted. Single-page-prototype residue; `workspace/uid-183-handoff.md`
  // and `workspace/uid-349-handoff.md` reached the same conclusion
  // independently. The canonical projects surface is the comp's own dedicated
  // "All projects" screen (L1620+, `isWorkspaces`) = `/projects`
  // (`AllWorkspacesPage`).
  //
  // The filter menu, by contrast, IS in the comp (`libProjMenu`) — so this
  // fetch stays. Do not remove it along with the tab.
  const { state: workspaceListState } = useWorkspaceList()
  const projectOptions = workspaceListState.status === 'ready' ? workspaceListState.workspaces : []

  // UID-654 (table-responsiveness pass) — `search` itself stays instant (the
  // input above is still wired straight to `setSearch`, so typing never
  // lags), but the QUERY this hook sends to the real `GET /parts?q=...`
  // backend below debounces it first: undebounced, every keystroke fired a
  // real network request. `useDebouncedValue` is the SAME shared hook
  // `use-add-parts-search.ts`'s search-input debounce and
  // `StagingPage.tsx`'s announcement debounce both build on
  // (`verticals/uploads/use-debounced-value.ts`), at this codebase's existing
  // 300ms search-debounce convention (`SEARCH_DEBOUNCE_MS`,
  // `use-add-parts-search.ts`).
  const debouncedSearch = useDebouncedValue(search, 300)
  const {
    state,
    refetch,
    addPartsToRfq,
    reorder,
    removePart,
    loadMore,
    isLoadingMore,
    loadMoreError,
    loadMoreErrorIsCursorRejection,
  } = usePartsLibrary(
    { q: debouncedSearch, status: statusFilter, sort: sortKey, groupBySession },
    service,
  )
  // UID-603 (ADR-0176 D1.4) — the "My parts" tab's infinite-scroll trigger.
  // Gated on there actually being a next page, so a fully-loaded (or
  // unpaginated-response) library mounts no `IntersectionObserver` at all.
  const hasNextPartsPage = state.status === 'ready' && state.listing.nextCursor != null
  // Code-review C1 (third pass) — `recheckKey` forces the observer to
  // re-check the sentinel's current intersection state every time a page is
  // appended, covering the case where the newly-appended page is ALSO
  // filtered away by the client-side `projectFilter` (below — `debouncedSearch`
  // itself is a SERVER-side `q` param that resets/refetches page 1, not a
  // client filter over already-loaded rows), leaving the sentinel still
  // intersecting without ever crossing a fresh threshold,
  // which would otherwise stall pagination one page in.
  //
  // Review W2 (fifth pass) — this used to key on `state.listing.parts.length`
  // (a loaded-item count), on the theory that `dedupeAppend` only ever unions
  // pages so a flat count means "the visible set genuinely didn't change
  // either." That conflates "visible set unchanged" with "nothing more to
  // fetch": a page that fully dedupes against already-loaded rows leaves the
  // count flat even though `nextCursor` advanced and more rows remain, so the
  // observer is never recreated and pagination can still stall — the exact
  // class of bug H4 fixed on `RfqListPage`/`NewRfqModal`/`WorkspacePage`.
  // `nextCursor` is non-lossy (see the hook's `recheckKey` doc comment) and
  // now matches all three of those call sites.
  const loadMoreSentinelRef = useLoadMoreSentinel(loadMore, hasNextPartsPage, {
    recheckKey: state.status === 'ready' ? state.listing.nextCursor : null,
  })
  // UID-290 — the BOMs tab (and its tab-badge count) is fed from the
  // dedicated `GET /boms` endpoint, NOT `state.listing.boms`/`counts.boms`
  // above (the pg library repository hard-codes `GET /parts`'s `boms` field
  // to `[]`, which is the root cause of the tab always showing "No BOMs
  // yet" even when BOMs exist). Own loading/error state, fetched alongside
  // the parts listing rather than gated on the tab being active.
  const {
    state: bomsState,
    refetch: refetchBoms,
    updateStatus: updateBomStatus,
  } = useBoms(bomsService)
  const navigate = useNavigate()
  // UID-402 — carries Triage's "Review in library" hand-off payload; see
  // `handoffPartIds` below.
  const location = useLocation()
  const { showToast } = useToast()
  const { setLineCount } = useCartCount()
  const rfqBundle = useRfqBundle()

  const allParts = state.status === 'ready' ? state.listing.parts : []
  const projectFilteredParts = projectFilter
    ? allParts.filter((row) => row.projects?.some((w) => w.id === projectFilter))
    : allParts

  /**
   * UID-402 (ADR-0124) — the receiving half of Triage's "Review in library"
   * hand-off. `StagingPage` confirms its session's parts into the Library and
   * navigates here with the resulting Library ids on router `location.state`;
   * this pins them to the top of the list, marks their rows, and counts them in
   * a banner.
   *
   * WHY THIS IS NEEDED AT ALL: server ordering is a flat `updatedAt DESC`
   * (`library-store.ts`'s `sortRecords`). A just-confirmed part therefore lands
   * only APPROXIMATELY near the top — interleaved with anything else edited
   * since, unlabelled and uncounted. "Near the top of a long list, looking like
   * everything else" is exactly the reported bug ("doesn't actually surface the
   * resolved parts"), so approximate recency is not a fix.
   *
   * WHY CLIENT-SIDE: this follows the precedent `projectFilter` above already
   * sets (see its doc comment) — narrow/reorder the fetched set in the page
   * rather than pushing a new field onto `LibraryListingQuery` and re-fetching.
   * A "pin these specific ids" ordering is a one-shot presentation concern for
   * a single navigation, not a query the BFF should learn.
   *
   * CAPTURED ONCE, at mount — a `useState` lazy initializer rather than a
   * `useMemo` over `location.state`, so the highlight SURVIVES the user then
   * searching/sorting/grouping within the page (each of which re-runs the fetch
   * and re-renders) instead of being recomputed from whatever `location`
   * currently holds.
   */
  const [handoffPartIds] = useState<ReadonlySet<string>>(() => {
    const handoff = location.state as { highlightPartIds?: readonly string[] } | null
    return new Set(handoff?.highlightPartIds ?? [])
  })

  /**
   * Gate-5 W3 — CONSUME the payload: strip it from the history entry once read.
   *
   * Capturing into mount-scoped state was necessary but NOT sufficient, and the
   * first pass's claim that it gave the highlight a "mount-scoped lifetime" was
   * simply wrong. `location.state` belongs to the HISTORY ENTRY, not to the
   * component: it survives unmount, and the browser restores it on reload. So
   * navigating away and pressing Back — or just reloading — remounted this page,
   * re-read the same ids, and re-pinned parts from an upload the user had long
   * finished with, with no way to dismiss it. A one-shot hand-off has to be
   * consumed, exactly like a flash message.
   *
   * `replace: true` so this rewrites the current entry rather than pushing a new
   * one (a push would put a `/parts`-with-state entry back into the Back stack,
   * recreating the bug one step further away). `state: null` drops the whole
   * payload — nothing else routes to `/parts` with state today, so there is no
   * sibling key to preserve.
   *
   * Terminates by construction: after the replace, `location.state` is null, the
   * effect re-runs on the new location and returns at the guard. The already-
   * captured `handoffPartIds` above are untouched, so the visible highlight is
   * unaffected — pinned by a test that asserts both the strip AND the surviving
   * banner/row mark.
   */
  useEffect(() => {
    const handoff = location.state as { highlightPartIds?: readonly string[] } | null
    if (!handoff?.highlightPartIds) return
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
  }, [location, navigate])

  /** Handed-over ids that actually resolved to a row in this listing. An id
   * that matches nothing (a stale hand-off, or a part since removed) must not
   * inflate the banner's count or imply a row exists — so the count is derived
   * from the RESOLVED rows, never from the incoming id list's length. */
  const handoffRows = projectFilteredParts.filter((row) => handoffPartIds.has(row.sourceable.id))

  /** Pinned-first ordering. Stable within each group: both partitions keep the
   * listing's own order, so the pin changes WHICH rows come first and nothing
   * else about the sort the user chose. */
  const parts =
    handoffRows.length === 0
      ? projectFilteredParts
      : [
          ...handoffRows,
          ...projectFilteredParts.filter((row) => !handoffPartIds.has(row.sourceable.id)),
        ]
  /**
   * UID-603 fix — this used to be `parts.length`, which was correct
   * pre-pagination (the fetch always loaded the WHOLE library) but is now
   * just "how many rows infinite-scroll has loaded so far": it grows on
   * every `loadMore`, so a search matching 400 parts would visibly climb
   * 50, 100, 150… as the user scrolls instead of reading "400" from the
   * first page. Read the server's `totalCount` instead — it is the true
   * count for the current `q`/`status` filter, independent of how many
   * pages have been fetched (ADR-0176 D4).
   *
   * EXCEPTION: `projectFilter` is a client-only narrowing over whatever
   * rows have loaded so far (see `projectFilteredParts` above) — the server
   * has no `totalCount` for "this filter AND this project".
   *
   * Code-review W5 correction — the PRIOR version of this comment claimed
   * this fallback "has the same pre-existing... property this page already
   * had for `projectFilter` before UID-603." That was false: pre-UID-603
   * the fetch was UNBOUNDED (the whole tenant library, every time), so
   * `projectFilteredParts`/`parts.length` really were the true count for
   * "this filter AND this project" — there was no undercount to have. It is
   * a genuinely NEW limitation UID-603 introduces: while a project filter is
   * active AND more pages remain (`hasNextPartsPage` below), the true count
   * for that project may exceed what's been scrolled into view so far, and
   * this reads as "N of M loaded" (below) rather than a bare, seemingly
   * final number, precisely so it doesn't misrepresent a partial scan as
   * the whole answer.
   */
  const partsCount =
    projectFilter === null && state.status === 'ready' && state.listing.totalCount !== undefined
      ? state.listing.totalCount
      : parts.length
  /** Code-review W5 — while `projectFilter` is active and more pages remain
   * unfetched, a bare count reads as final when it may not be: this renders
   * "N of M loaded" instead ("N matching this project, out of M rows loaded
   * from your library so far — keep scrolling for more"), rather than
   * silently letting `partsCount` above pass for a complete answer. Once
   * there's no next page (or no project filter is active), the plain count
   * is already the true, final answer and this collapses back to it. */
  const partsCountLabel =
    projectFilter !== null && hasNextPartsPage
      ? `${partsCount} of ${allParts.length} loaded`
      : `${partsCount} part${partsCount === 1 ? '' : 's'}`
  const boms = bomsState.status === 'ready' ? bomsState.boms : []
  const counts = {
    parts: state.status === 'ready' ? state.listing.counts.parts : 0,
    boms: boms.length,
  }
  // UID-290 code-review follow-up — the BOMs tab badge stays OFF (no count
  // shown, not even "0") until `bomsState` actually resolves `ready`, so a
  // slow or failed `GET /boms` never flashes a misleading "0" badge (the
  // parts tab already reads this way in spirit: `state.status === 'ready'`
  // gates `counts.parts` above).
  const bomsBadge =
    bomsState.status === 'ready' ? ({ kind: 'count', count: counts.boms } as const) : undefined

  // UID-257 — the selection bar's gating reads the SELECTED rows' lifecycle
  // state through the shared `resolveRowStatus` helpers: "Request quote for N"
  // is disabled while any selected part still needs configuration (§8), and
  // "Add N to cart" is enabled only when every selected part is priced (§6/§8).
  const selectedRows = allParts.filter((row) => selectedIds.has(row.sourceable.id))
  const requestQuoteDisabled = anyNeedsConfig(selectedRows)
  const addToCartDisabled = !allPriced(selectedRows)
  // UID-3 — the selection bar now conditionally RENDERS each action by the
  // selection's composition (design `Fictiv Platform.dc.html:10475-10479`),
  // instead of always showing all three and greying the inapplicable ones:
  //   - Configure      → only when a selected part needs configuration
  //   - Request quote  → only when a selected part needs a quote (`libSelHasQuote`)
  //   - Add N to cart  → only when EVERY selected part is priced (`libSelAllPriced`);
  //                      naturally hidden the moment any RFQ/needs-quote part is in
  //                      the selection.
  // The one overlap the design still greys (rather than hides) is kept: when a
  // selection has BOTH needs-quote AND needs-config parts, Request quote shows
  // but greyed with its "configure first" tooltip (`requestQuoteDisabled`).
  const selectionNeedsConfig = anyNeedsConfig(selectedRows)
  const selectionNeedsQuote = anyNeedsQuote(selectedRows)
  const selectionAllPriced = allPriced(selectedRows)
  // UID-418 — the "Configure N" / "Request quote for N" CTAs act on (and count)
  // only the SUBSET of the selection each applies to (rows needing config /
  // rows needing a quote), not the whole selection (`selectedIds.size`), which
  // previously over-counted whenever the selection mixed lifecycle states. The
  // count and the handler share these arrays, so "Configure 1" configures
  // exactly 1 part and "Request quote for 1" bundles exactly 1 part — the
  // ready/priced rows in the same selection are left untouched by these two
  // actions (they route through "Add N to cart" instead).
  const needsConfigRows = selectNeedsConfig(selectedRows)
  const needsQuoteRows = selectNeedsQuote(selectedRows)
  const needsConfigCount = needsConfigRows.length
  const needsQuoteCount = needsQuoteRows.length

  /**
   * UID-470 — row checkbox click handler. A plain click/keyboard toggle
   * (`shiftKey` false/omitted) flips just `id` and becomes the new
   * selection anchor — ordinary behavior. A shift-click (mouse OR the
   * Shift+Space keyboard equivalent — see `PartsTable.tsx`'s row checkbox,
   * whose `onClick` reads `event.shiftKey` off the SAME kind of `click`
   * event real browsers dispatch for either input modality) with a live
   * anchor applies the RESULT of toggling `id` — i.e. its opposite-of-
   * current state, matching Gmail/Finder: shift-clicking an unchecked row
   * selects the whole range, shift-clicking an already-checked one
   * deselects it — to every row between the anchor and `id`, inclusive,
   * computed over `parts`: the CURRENTLY RENDERED order (already narrowed
   * by the project filter, sorted/searched server-side, and handoff-pinned
   * — see `parts`'s own derivation above), never the raw unfiltered
   * dataset. The anchor then moves to `id`.
   *
   * Anchor-reset decision (documented here, not hidden in a side-effect):
   *   - Re-sorting/re-filtering while the anchor row is STILL in `parts`
   *     does NOT reset it. The range is recomputed fresh, by id → index
   *     lookup, on every click — so shift-clicking after a sort/filter
   *     change selects exactly what's on screen NOW, matching the spec's
   *     "respect the currently rendered order" requirement without any
   *     extra bookkeeping.
   *   - If the anchor row has been filtered OUT of `parts` entirely,
   *     `indexOf` returns -1 below and this falls through to the plain-
   *     toggle-and-re-anchor path — there is no valid range to compute, so
   *     the click degrades to selecting just the clicked row.
   *   - Explicitly CLEARING the selection (`clearSelection`, every call
   *     site in this file that used to reset `selectedIds` alone) resets
   *     the anchor too. Clearing is a "start over" gesture; leaving a
   *     stale anchor in place would let a later shift-click resurrect a
   *     range relative to a selection the user just wiped, which is
   *     surprising rather than sensible.
   */
  function toggleSelect(id: string, shiftKey = false) {
    if (shiftKey && selectionAnchorId !== null) {
      const ids = parts.map((row) => row.sourceable.id)
      const anchorIndex = ids.indexOf(selectionAnchorId)
      const targetIndex = ids.indexOf(id)
      if (anchorIndex !== -1 && targetIndex !== -1) {
        const start = Math.min(anchorIndex, targetIndex)
        const end = Math.max(anchorIndex, targetIndex)
        const rangeIds = ids.slice(start, end + 1)
        const willSelect = !selectedIds.has(id)
        setSelectedIds((prev) => {
          const next = new Set(prev)
          for (const rangeId of rangeIds) {
            if (willSelect) next.add(rangeId)
            else next.delete(rangeId)
          }
          return next
        })
        setSelectionAnchorId(id)
        return
      }
      // Anchor no longer in the currently-rendered order — fall through to
      // a plain toggle below rather than computing a meaningless range.
    }
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setSelectionAnchorId(id)
  }

  /** UID-470 — the one place selection is fully wiped clears the range
   * anchor alongside it; see `toggleSelect`'s doc comment for why. */
  function clearSelection() {
    setSelectedIds(new Set())
    setSelectionAnchorId(null)
  }

  function toggleSelectAll(ids: readonly string[], select: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (select) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }

  // UID-112: queues the selected rows into the cross-screen RFQ bundle.
  //
  // UID-309 (reference `Fictiv Platform.dc.html` parity — overrides this
  // handler's PRIOR "queue+navigate straight into `/rfq/new`" behavior):
  // the reference never routes away from the Library the moment a part is
  // queued. Instead a persistent "RFQ bundle" banner (rendered above, gated
  // only on `rfqBundle.items.length > 0 && canSubmitRfq` — see that render
  // site's own doc comment for why it is deliberately NOT also gated on
  // `selectedIds.size === 0`) stays on screen so the customer can keep
  // browsing and queue MORE parts across multiple selections before
  // choosing to navigate — only that banner's own "Review & request quote"
  // button does the `navigate('/rfq/new')` now. This handler's job shrinks
  // to: queue, clear the current selection (so the bulk bar steps back down
  // to its "0 selected" no-op state), fire the best-effort audit flag.
  //
  // Code-review follow-up (S1/S2, still true): the QUEUE half is the
  // ESSENTIAL path and must not be gated behind a network call.
  // `addPartsToRfq` (`POST /parts/rfq`) is a separate, best-effort
  // server-side flag for other consumers/audit — it is NOT wired to
  // anything the real RFQ flow reads (BomDetailPage's equivalent CTAs
  // don't call it at all, an intentional, tracked inconsistency pending a
  // follow-up to either retire this endpoint or promote it into a real
  // server draft). It must never block or fail the user's actual "add to
  // RFQ" action, so it's fired-and-forgotten AFTER the essential queue
  // step, with its own error swallowed rather than surfaced (there is
  // nothing useful to show the user about an audit flag).
  function handleAddToRfq() {
    // UID-284 fix: this operates on the SELECTED rows read from the unfiltered
    // `selectedRows` (not the `projectFilter`-narrowed `parts`), so a
    // selected-but-filtered-out row is never silently dropped.
    //
    // UID-418: it now queues ONLY the subset that actually needs a (re-)quote
    // (`needsQuoteRows`) — the same rows the "Request quote for N" CTA counts —
    // rather than the whole selection. A `ready`/priced row that happened to be
    // co-selected is no longer dumped into the RFQ bundle; it routes through
    // "Add N to cart" instead. The full selection is still cleared afterwards
    // (clicking a bulk action concludes the current selection).
    const queued = needsQuoteRows
    if (queued.length === 0) return
    const addedCount = queued.length
    // UID-335 follow-up — `GET /parts` resolves a real/generated thumbnail
    // onto `row.thumbnailUrl`, a SIBLING field of `row.sourceable` (never
    // onto `sourceable.imageRef` itself). See `toSafeImageRef`'s doc
    // comment for why the merge needs the root-relative-vs-absolute
    // distinction it applies.
    // UID-552 (ADR-0170) — `beginDraft`, not `addItems`: every "Create RFQ"/
    // "Add to RFQ" entry point must start a genuinely NEW draft, never merge
    // into whatever slot happened to be active. This banner stays on the
    // Library page (ADR-0102 — deliberately does NOT navigate), so the newly
    // begun slot simply becomes the one the persistent banner reflects.
    rfqBundle.beginDraft(
      queued.map((row) => {
        const imageRef = toSafeImageRef(row.thumbnailUrl, row.sourceable.id)
        return {
          sourceable: imageRef !== undefined ? { ...row.sourceable, imageRef } : row.sourceable,
          name: row.sourceable.name ?? 'Part',
          sku: row.sourceable.catalogSku ?? null,
          // UID-530 — carry the quantity this row is SHOWING into the bundle, so
          // the compose screen seeds `quantityOverrides` from it instead of
          // silently defaulting the line to 1. `beginDraft` is first-wins on its
          // OWN seed batch, so this can never overwrite a quantity the buyer has
          // already edited on this same slot.
          quantity: row.quantity,
        }
      }),
    )
    clearSelection()
    // Gate 5 code review (S6) — the announcement covers the async-invisible
    // state change for screen-reader users, and the flag hands off to the
    // effect above once the banner has actually (re)mounted in the bulk
    // bar's vacated slot.
    setBundleAnnouncement(
      `${addedCount} part${addedCount === 1 ? '' : 's'} added to your RFQ bundle.`,
    )

    void addPartsToRfq(queued.map((row) => row.sourceable.id)).catch(() => {
      // Best-effort audit flag only — see comment above. Nothing to surface.
    })
  }

  // UID-159 (A3, ADR-0058 §3): a single row's Qty-cell popover, when the
  // active quantity is above the curve's cap, offers "Request volume RFQ"
  // instead of a fabricated price/Add-to-cart. Reuses the SAME
  // queue-into-`/rfq/new` essential path as `handleAddToRfq` above (queue +
  // navigate is essential and must not be gated behind the best-effort
  // audit-flag network call — same S1/S2 rationale).
  function handleRequestVolumeRfq(row: LibraryPartRow) {
    const imageRef = toSafeImageRef(row.thumbnailUrl, row.sourceable.id)
    // UID-552 (ADR-0170) — `beginDraft`, not `addItems`: see `handleAddToRfq`'s
    // comment above.
    rfqBundle.beginDraft([
      {
        sourceable: imageRef !== undefined ? { ...row.sourceable, imageRef } : row.sourceable,
        name: row.sourceable.name ?? 'Part',
        sku: row.sourceable.catalogSku ?? null,
        // UID-530 — see handleAddToRfq. Load-bearing here in particular: this CTA
        // exists BECAUSE the active quantity is above the volume curve's cap, so
        // arriving on the compose screen with a quantity of 1 contradicts the very
        // reason the buyer was offered a volume RFQ.
        quantity: row.quantity,
      },
    ])
    navigate('/rfq/new')

    void addPartsToRfq([row.sourceable.id]).catch(() => {
      // Best-effort audit flag only — see handleAddToRfq's comment above.
    })
  }

  async function handleReorder(sourceableId: string) {
    setReorderingId(sourceableId)
    try {
      const { cartLineCount } = await reorder(sourceableId)
      // Push the authoritative count to the top-bar badge (the Cart screen stays the
      // source of truth; this just keeps the badge fresh after an off-cart reorder).
      setLineCount(cartLineCount)
      showToast('Reorder started; review it in your cart.', 'success')
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : 'We couldn’t add this part to your cart. Try again.',
        'error',
      )
    } finally {
      setReorderingId(null)
    }
  }

  /** UID-422 "Remove from library" (⋮ row menu). Mirrors `WorkspacePage.tsx`'s
   * `handleDelete`: re-entry guard against a fast double-click on the
   * `ConfirmDialog`'s own confirm button, error surfaced inside the still-open
   * dialog, and on success the dialog closes and the listing refetches (the
   * removed part's row disappears — `removePart`, above, already calls `refetch`). */
  async function handleConfirmRemove() {
    if (removeTarget === null || removeBusy) return
    setRemoveBusy(true)
    setRemoveError(undefined)
    try {
      await removePart(removeTarget.sourceable.id)
      showToast(
        `Removed "${removeTarget.sourceable.name ?? 'this part'}" from your library.`,
        'success',
      )
      setRemoveTarget(null)
    } catch (error) {
      setRemoveError(
        error instanceof Error ? error.message : 'We couldn’t remove this part. Try again.',
      )
    } finally {
      setRemoveBusy(false)
    }
  }

  // UID-170/F2 bulk bar "Add {n} to cart" — loops the SAME real reorder/
  // add-to-cart call `handleReorder` uses across every selected row (there
  // is no bulk-cart endpoint), and refreshes the top-bar badge once at the
  // end from the authoritative response rather than per-row.
  async function handleBulkAddToCart() {
    setBulkCartBusy(true)
    try {
      let lineCount: number | undefined
      for (const id of selectedIds) {
        lineCount = (await reorder(id)).cartLineCount
      }
      if (lineCount !== undefined) setLineCount(lineCount)
      showToast('Added to cart; review it in your cart.', 'success')
      clearSelection()
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'We couldn’t add these parts to your cart.',
        'error',
      )
    } finally {
      setBulkCartBusy(false)
    }
  }

  // UID-257 bulk bar "Configure N": opens the bulk-configuration workflow for
  // EVERY selected part (design handoff §8), replacing the old exactly-one-row
  // no-op. Snapshots the current selection's rows so the applied batch — and
  // the modal's row-derived advisory/price preview — are stable.
  //
  // UID-46 (demo-safety): the batch is a sequential loop-PATCH with no real
  // bulk endpoint backing it (`bulk-configure-service`'s own doc comment) —
  // an unbounded selection would fire an unbounded, uncancellable run of
  // PATCHes. Gate BEFORE opening the modal with an explanatory toast rather
  // than silently truncating the selection.
  // Shared cap check for both bulk-configure entry points ("Configure N" and
  // "Apply to multiple parts") — returns false (and toasts) when the selection
  // exceeds MAX_BULK_CONFIGURE_PARTS. Reads the constant symbolically so it
  // tracks any retune (was 50, now 120 for a 90-part demo BOM).
  function withinBulkConfigureCap(count: number): boolean {
    if (count > MAX_BULK_CONFIGURE_PARTS) {
      showToast(`Select ${MAX_BULK_CONFIGURE_PARTS} or fewer parts to configure at once.`, 'error')
      return false
    }
    return true
  }

  // UID-418 — configure ONLY the selected rows that actually need config
  // (`needsConfigRows`), not the whole selection. The cap is checked against
  // that same subset, and the modal's "Configure N parts" title / "Apply to N"
  // button now agree with the bar's "Configure N" CTA count.
  function handleBulkConfigure() {
    if (needsConfigRows.length === 0) return
    if (!withinBulkConfigureCap(needsConfigRows.length)) return
    setBulkConfigureRows(needsConfigRows)
  }

  // UID-418 — the "Add to project" overlay (`AddToProjectMenu`) links the whole
  // current selection to the chosen/created project itself; this fires once the
  // batch lands. Unlike Configure/Request quote, add-to-project applies to any
  // selected part regardless of lifecycle state.
  function handleProjectLinked(workspaceName: string, count: number) {
    showToast(`Added ${count} part${count === 1 ? '' : 's'} to “${workspaceName}”.`, 'success')
    clearSelection()
    refetch()
  }

  // UID-257 — applies the collected configuration to every selected part via
  // the loop-PATCH bulk service (each PATCH runs a server-side re-price), then
  // refetches so the listing picks up new prices/statuses. Partial failures
  // surface which parts didn't take rather than silently rolling back the
  // succeeded ones.
  async function handleBulkConfigureSubmit(request: EditPartConfigRequest) {
    const ids = (bulkConfigureRows ?? []).map((row) => row.sourceable.id)
    if (ids.length === 0) return
    const { succeeded, failed } = await bulkConfigureParts(ids, request)
    if (failed.length === 0) {
      showToast(
        `Configured ${succeeded.length} part${succeeded.length === 1 ? '' : 's'}.`,
        'success',
      )
      clearSelection()
    } else if (succeeded.length === 0) {
      // Code-review follow-up: surface the SPECIFIC per-part reason
      // (`bulk-configure-service` already computes one), not just an
      // aggregate count — a failed batch commonly fails for the same
      // reason, so the first one makes a retry actionable.
      showToast(
        `We couldn’t apply this configuration: ${failed[0]?.message ?? 'Try again.'}`,
        'error',
      )
    } else {
      showToast(
        `Configured ${succeeded.length} of ${ids.length}. ${failed.length} couldn’t be applied: ${failed[0]?.message ?? 'Try again.'}`,
        'error',
      )
      // Keep the parts that failed selected so the user can retry just those.
      setSelectedIds(new Set(failed.map((f) => f.partId)))
    }
    refetch()
    setBulkConfigureRows(null)
  }

  // UID-46 — "Apply to multiple parts" (the row-level `EditConfigurationModal`
  // button, enabled only when >1 part is selected — see its own render below):
  // applies THAT modal's current in-progress configuration to the caller's
  // active multi-selection via the SAME loop-PATCH bulk service "Configure N"
  // uses, then reports back with an "N parts updated" confirmation.
  async function handleApplyToMultiple(request: EditPartConfigRequest) {
    const ids = [...selectedIds]
    if (ids.length < 2) return
    // UID-324 — same cap as "Configure N": this second bulk entry point rides
    // the identical uncapped loop-PATCH, so it must gate on the cap too.
    if (!withinBulkConfigureCap(ids.length)) return
    setApplyToMultipleBusy(true)
    try {
      const { succeeded, failed } = await bulkConfigureParts(ids, request)
      if (failed.length === 0) {
        showToast(`${succeeded.length} parts updated.`, 'success')
        setConfigPart(null)
      } else if (succeeded.length === 0) {
        showToast(
          `We couldn’t apply this configuration: ${failed[0]?.message ?? 'Try again.'}`,
          'error',
        )
      } else {
        showToast(
          `Updated ${succeeded.length} of ${ids.length}. ${failed.length} couldn’t be applied: ${failed[0]?.message ?? 'Try again.'}`,
          'error',
        )
      }
      refetch()
    } finally {
      setApplyToMultipleBusy(false)
    }
  }

  // UID-376 — "Create revision" from the row ⋮ menu. Fetches the full
  // `PartDetail` for this row (the listing's `LibraryPartRow` doesn't carry
  // everything `CreateRevisionModal` needs) via the SAME
  // `service.getPartDetail` seam `usePartDetail` uses, then opens the modal
  // once it resolves — same load-then-open posture as the page's other
  // on-demand pickers.
  async function handleOpenCreateRevision(item: LibraryPartRow) {
    try {
      setRevisionPart(await service.getPartDetail(item.sourceable.id))
    } catch {
      showToast('We couldn’t load this part to create a revision. Try again.', 'error')
    }
  }

  // dc.html:6542 — clearBundle() fires directly (setState + flash), no
  // confirmation step. Matches `RfqDraftPanel.tsx`'s equivalent discard
  // action, which was brought in line with the design the same way.
  return (
    <section aria-labelledby="parts-library-heading" className="parts-library-page">
      <div className="parts-library-header">
        <div className="parts-library-header-copy">
          <h1 id="parts-library-heading" className="display-heading">
            Part library
          </h1>
          <p className="parts-library-subtitle">
            Everything you’ve uploaded or purchased — reorder in a click, or roll parts into a BOM.
          </p>
        </div>
      </div>

      {/* Gate 5 code review (S6) — the RFQ bundle's own polite live region,
       * same sr-only pattern as `RfqListPage.tsx`'s `liveMessage`/
       * `draftAnnouncement` — announces additions/clears that the banner's
       * visual (re)mount alone doesn't reliably convey to assistive tech. */}
      <p aria-live="polite" className="sr-only">
        {bundleAnnouncement}
      </p>

      <div className="parts-library-tabs">
        <Tabs
          items={[
            { id: 'parts', label: 'My parts', badge: { kind: 'count', count: counts.parts } },
            { id: 'boms', label: 'BOMs', ...(bomsBadge ? { badge: bomsBadge } : {}) },
          ]}
          activeId={tab}
          onChange={(id) => setTab(id as TabId)}
          aria-label="Parts library sections"
        >
          {(activeId) => {
            if (activeId === 'boms') {
              // UID-290 — the BOMs tab has its OWN loading/error state, separate
              // from the parts listing's `state` above, since it's now a
              // completely independent `GET /boms` fetch.
              if (bomsState.status === 'loading') {
                return <PartsLibrarySkeleton />
              }

              if (bomsState.status === 'error') {
                return (
                  <EmptyState
                    title="We couldn’t load your BOMs"
                    detail={bomsState.message}
                    action={<Button onClick={refetchBoms}>Try again</Button>}
                  />
                )
              }

              if (boms.length === 0) {
                return (
                  <EmptyState
                    title="No BOMs yet"
                    detail="Upload a bill of materials to start managing assemblies here."
                    action={<Button onClick={() => navigate('/uploads')}>Upload CAD</Button>}
                  />
                )
              }
              return (
                <BomCardGrid
                  items={boms}
                  onOpen={(id) => navigate(`/parts/boms/${id}`)}
                  onStatusChange={updateBomStatus}
                />
              )
            }

            if (state.status === 'loading') {
              return <PartsLibrarySkeleton />
            }

            if (state.status === 'error') {
              return (
                <EmptyState
                  title="We couldn’t load your parts library"
                  detail={state.message}
                  action={<Button onClick={refetch}>Try again</Button>}
                />
              )
            }

            if (counts.parts === 0) {
              return (
                <EmptyState
                  icon={<UploadOutlined aria-hidden="true" />}
                  title="You haven’t uploaded or purchased any parts yet"
                  detail="Upload CAD to get started."
                  action={<Button onClick={() => navigate('/uploads')}>Upload CAD</Button>}
                />
              )
            }

            return (
              <div className="parts-library-toolbar-and-table">
                <div className="parts-library-toolbar">
                  <div className="parts-search-wrap">
                    <span className="parts-search-icon" aria-hidden="true">
                      <SearchOutlined />
                    </span>
                    <label className="sr-only" htmlFor="parts-search">
                      Search parts
                    </label>
                    <input
                      id="parts-search"
                      type="search"
                      className="parts-search-input"
                      placeholder="Search parts by name, MPN, or category"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                    {/* UID-225 design-review punch list item 10 — a trailing
                      clear control shown only once the box has a value
                      (`Fictiv Platform.dc.html` ~L1168-1170), not relying on
                      `type="search"`'s inconsistent-across-browsers native
                      clear affordance. */}
                    {search.length > 0 && (
                      <button
                        type="button"
                        className="parts-search-clear"
                        aria-label="Clear search"
                        onClick={() => setSearch('')}
                      >
                        <CloseOutlined aria-hidden="true" />
                      </button>
                    )}
                  </div>
                  <StatusFilterBar active={statusFilter} onChange={setStatusFilter} />
                  <ProjectFilterMenu
                    options={projectOptions}
                    value={projectFilter}
                    onChange={setProjectFilter}
                  />
                  <span className="parts-library-toolbar-spacer" />
                  <span className="parts-library-count">{partsCountLabel}</span>
                  <fieldset className="parts-sort-fieldset">
                    {/* UID-225: the design's sort-chips container
                      (`Fictiv Platform.dc.html` ~L1186-1188) has no visible
                      "Sort" caption beside it — kept for assistive tech via
                      the fieldset's own accessible name, just visually
                      hidden (`sr-only`, not removed) rather than shown as
                      body text. */}
                    <legend className="sr-only">Sort</legend>
                    <button
                      type="button"
                      className={
                        sortKey === 'recent'
                          ? 'parts-sort-btn parts-sort-btn-active'
                          : 'parts-sort-btn'
                      }
                      aria-pressed={sortKey === 'recent'}
                      onClick={() => setSortKey('recent')}
                    >
                      Recent
                    </button>
                    <button
                      type="button"
                      className={
                        sortKey === 'name'
                          ? 'parts-sort-btn parts-sort-btn-active'
                          : 'parts-sort-btn'
                      }
                      aria-pressed={sortKey === 'name'}
                      onClick={() => setSortKey('name')}
                    >
                      Name
                    </button>
                  </fieldset>
                  <label className="parts-library-checkbox-toggle">
                    <input
                      type="checkbox"
                      checked={groupBySession}
                      onChange={(event) => setGroupBySession(event.target.checked)}
                    />
                    {/* UID-225: an icon+label toggle pill (`Fictiv
                      Platform.dc.html` ~L1189), not a bare checkbox — the
                      real checkbox above stays for a11y (role="checkbox",
                      its own focus ring), this just adds the decorative
                      Layers-equivalent glyph beside the label text. */}
                    <IconShipTogether size={14} />
                    Group by session
                  </label>
                </div>

                {parts.length === 0 ? (
                  // Code-review W5 — `projectFilter` is a client-only
                  // narrowing over whatever pages have loaded SO FAR (see
                  // `projectFilteredParts`'s doc comment above): while a
                  // project filter is active and more pages remain
                  // (`hasNextPartsPage`), zero matches among the rows loaded
                  // so far does NOT mean zero matches exist — the still-
                  // unfetched pages haven't been checked yet. Declaring "No
                  // parts match your search" here would be a false negative;
                  // this branch keeps the sentinel below actively loading
                  // (it's rendered unconditionally on `hasNextPartsPage`,
                  // regardless of which branch renders here) and shows an
                  // honest in-progress state instead of a premature "no
                  // matches" verdict.
                  projectFilter !== null && hasNextPartsPage ? (
                    <EmptyState
                      icon={
                        <span className="parts-empty-search-icon">
                          <SearchOutlined aria-hidden="true" />
                        </span>
                      }
                      title="Still checking your library for matches…"
                      detail="No matches in what's loaded so far — more of your library is still loading."
                    />
                  ) : (
                    // UID-225 design-review punch list item 9 — design copy +
                    // a 34x34 search-glyph icon, replacing this screen's prior
                    // generic "no filter match" text (this EmptyState instance
                    // only; the "no parts uploaded yet"/load-failure empty
                    // states above are untouched).
                    <EmptyState
                      icon={
                        <span className="parts-empty-search-icon">
                          <SearchOutlined aria-hidden="true" />
                        </span>
                      }
                      title="No parts match your search"
                      detail="Try a different name, MPN, or category."
                    />
                  )
                ) : (
                  <PartsTable
                    items={parts}
                    groupBySession={groupBySession}
                    // UID-402 — marks the rows handed over from Triage, so they
                    // are identifiable and not merely repositioned.
                    highlightedIds={handoffPartIds}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelect}
                    onToggleSelectAll={toggleSelectAll}
                    onOpen={(id) => navigate(`/parts/${id}`)}
                    onReorder={handleReorder}
                    reorderingId={reorderingId}
                    onRequestVolumeRfq={handleRequestVolumeRfq}
                    onEditConfig={setConfigPart}
                    // UID-376 — the design's row ⋮ menu carries EXACTLY three
                    // items, in this order: Share part / Create revision /
                    // Remove from library. "Edit configuration"/"Reorder"/
                    // "Add to project" are no longer offered from this menu
                    // (still reachable via the primary-action cell and the
                    // REQUIREMENTS cell's pencil icon, above).
                    //
                    // UID-422 — opens PartShareModal for this row (see
                    // `sharePart` state doc comment above).
                    onShare={setSharePart}
                    // UID-422 — opens the "Remove from library?" confirm dialog
                    // for this row (see `removeTarget` state doc comment above).
                    onRemoveFromLibrary={setRemoveTarget}
                    // Opens the SAME `CreateRevisionModal` `PartDetailPage`
                    // uses (UID-167/D3) — fetches the row's full `PartDetail`
                    // first (see `handleOpenCreateRevision`'s doc comment).
                    onCreateRevision={handleOpenCreateRevision}
                    // UID-405 (design-parity audit fix) — the "Find match"
                    // primary action for a `needs_match` row. Design
                    // `:8628`'s `it.onAction` for `needs-match` navigates to
                    // the catalog search and flashes a toast; this reuses
                    // the SAME `/catalog` route `PartsLibraryPage` already
                    // navigates to for the toolbar (`verticals/registry.tsx`
                    // `basePath:'/catalog'`), plus the equivalent toast.
                    onFindMatch={(item) => {
                      navigate('/catalog')
                      showToast(
                        `Search the catalog to match "${item.sourceable.name ?? 'this part'}"`,
                        'info',
                      )
                    }}
                  />
                )}

                {/* UID-603 (ADR-0176 D1.4) — infinite-scroll sentinel for the
                  "My parts" tab: an empty div the `IntersectionObserver`
                  watches, ~600px before it would actually enter the
                  viewport (see `useLoadMoreSentinel`'s doc comment) so the
                  next page is already loading before the shopper hits the
                  bottom. Renders nothing visible when there's no next page
                  — `hasNextPartsPage` gates both the observer and this
                  markup, so a fully-loaded library adds no DOM/observer at
                  all. `<output>` (implicit `role="status"`) + `aria-live` on
                  the "Loading more…" line announces the busy state to
                  screen-reader users without stealing focus; nothing here
                  participates in tab order. */}
                {hasNextPartsPage && (
                  <div ref={loadMoreSentinelRef} className="parts-load-more-sentinel">
                    <output aria-live="polite" className="parts-load-more-status">
                      {isLoadingMore ? 'Loading more parts…' : ''}
                    </output>
                    {loadMoreError && (
                      <div role="alert" className="parts-load-more-error">
                        <p>{loadMoreError}</p>
                        {/* M2 (code-review UID-603 fourth pass) — a cursor
                          the server has rejected as unresolvable
                          (`library-store.ts`'s deliberate 400) can never
                          succeed via `loadMore()`, which just re-issues the
                          SAME dead cursor forever. `loadMoreErrorIsCursorRejection`
                          distinguishes that case from a generic network/500
                          failure and retries via `refetch()` (reset to page
                          1) instead. */}
                        <button
                          type="button"
                          className="btn-link"
                          onClick={loadMoreErrorIsCursorRejection ? refetch : loadMore}
                        >
                          Try again
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* UID-257 — Library selection bar carries exactly the design's
                  three actions + Clear: Configure N / Request quote for N
                  (disabled while any selected part needs config) / Add N to
                  cart (enabled only when all selected are priced). "Add to RFQ
                  bundle" and "Add to project" no longer live here.
                  "Request quote for N" reuses the queue-into-`/rfq/new` path
                  (`handleAddToRfq`) — synchronous, so no in-flight state.

                  UID-3 — each action's HANDLER is now passed only when the
                  selection composition calls for it (see the
                  `selectionNeedsConfig`/`selectionNeedsQuote`/`selectionAllPriced`
                  derivation above), so `BulkActionBar` renders exactly the
                  buttons the design shows for that selection (`Fictiv
                  Platform.dc.html:10475-10479`) rather than always rendering all
                  three and greying the inapplicable ones. */}
                <BulkActionBar
                  selectedCount={selectedIds.size}
                  onConfigure={selectionNeedsConfig ? handleBulkConfigure : undefined}
                  configureLabel={`Configure ${needsConfigCount}`}
                  onRequestQuote={selectionNeedsQuote ? handleAddToRfq : undefined}
                  requestQuoteLabel={`Request quote for ${needsQuoteCount}`}
                  requestQuoteDisabled={requestQuoteDisabled}
                  requestQuoteDisabledReason="Configure every selected part before requesting a quote."
                  onAddToCart={selectionAllPriced ? handleBulkAddToCart : undefined}
                  cartLabel={`Add ${selectedIds.size} to cart`}
                  cartVariant="primary"
                  cartBusy={bulkCartBusy}
                  addToCartDisabled={addToCartDisabled}
                  addToCartDisabledReason="Every selected part must be priced before adding to cart."
                  addToProjectControl={
                    <AddToProjectMenu
                      partIds={[...selectedIds]}
                      workspaces={projectOptions}
                      onLinked={handleProjectLinked}
                    />
                  }
                  onClear={clearSelection}
                />
              </div>
            )
          }}
        </Tabs>
      </div>

      {/* UID-376: "Create revision" — the row ⋮ menu's real destination, the
          SAME `CreateRevisionModal` `PartDetailPage` opens, mounted once for
          the whole page and opened per-row once `handleOpenCreateRevision`
          resolves the full `PartDetail`. */}
      {revisionPart && (
        <CreateRevisionModal
          open
          onClose={() => setRevisionPart(null)}
          part={revisionPart}
          onCreated={() => refetch()}
        />
      )}

      {/* UID-257: "Configure N" bulk-configuration workflow — applies one
          configuration across every part selected when the bar's Configure
          action fired (ids snapshotted in `bulkConfigureIds`). */}
      {bulkConfigureRows && (
        <PartsBulkConfigureModal
          open
          onClose={() => setBulkConfigureRows(null)}
          parts={bulkConfigureRows}
          onSubmit={handleBulkConfigureSubmit}
        />
      )}

      {/* UID-163/C3: mounted once for the whole page, opened per-row via
          `PartsTable`'s primary-action "Configure" control and the
          REQUIREMENTS cell's pencil icon (`setConfigPart`) — UID-376 removed
          this modal's OWN "Edit configuration" entry from the ⋮ row menu
          (not part of the design), but both of those other entry points
          still open it exactly the same way.
          UID-46: "Apply to multiple parts" is wired only when the LIBRARY's
          own multi-selection has more than one part — with exactly 0 or 1
          selected, `onApplyToMultiple` is omitted so the button renders
          disabled with its built-in explanatory text (this modal's own
          convention; see its props doc). */}
      {configPart && (
        <EditConfigurationModal
          open
          onClose={() => setConfigPart(null)}
          part={configPart}
          onSaved={() => refetch()}
          applyToMultipleBusy={applyToMultipleBusy}
          // UID-506 — this modal has no cart-mutation logic of its own (see
          // its `onAddToCart` doc comment); without wiring it here, "Add to
          // cart" always rendered disabled, even once the part was priced.
          // Reuses the SAME real add-to-cart call the row-level "+" button
          // (`handleReorder`, via `PartsTable`'s `onReorder`) already uses —
          // no second cart-mutation path. The shell saves the current
          // configuration BEFORE calling this, so it always targets the
          // configuration on screen, not a stale one.
          onAddToCart={(part) => {
            void handleReorder(part.sourceable.id)
          }}
          {...(selectedIds.size > 1 ? { onApplyToMultiple: handleApplyToMultiple } : {})}
        />
      )}

      {sharePart && (
        <PartShareModal
          open
          onClose={() => setSharePart(null)}
          partId={sharePart.sourceable.id}
          partName={sharePart.sourceable.name ?? '(unnamed part)'}
        />
      )}

      {/* UID-422 "Remove from library" — design copy verbatim
          (`Fictiv Platform.dc.html` ~L4549-4551 `removeModal`). */}
      <ConfirmDialog
        open={removeTarget !== null}
        title="Remove from library?"
        description={`"${removeTarget?.sourceable.name ?? 'This part'}" and all of its revisions will be removed from your library. Purchase history and past orders are unaffected.`}
        confirmLabel={removeBusy ? 'Removing…' : 'Remove part'}
        onCancel={() => {
          setRemoveTarget(null)
          setRemoveError(undefined)
        }}
        onConfirm={handleConfirmRemove}
        error={removeTarget !== null ? removeError : undefined}
      />
    </section>
  )
}

/**
 * "All projects" toolbar filter (UID-225 visual-fidelity pass, `Fictiv
 * Platform.dc.html` ~L1177-1182 `libProjMenu`) — a button+menu in place of
 * the native `<select>` the build previously used, so it matches the
 * design's Layers-icon/label/chevron trigger. Same options/handler contract
 * as the `<select>` it replaces (`projectFilter`/`setProjectFilter` in
 * `PartsLibraryPage`); this only changes the rendering. A lightweight
 * disclosure — same blur/Escape-to-close pattern as `PartsTable.tsx`'s
 * `PartRowActions` ⋮ menu, rather than a global click listener.
 */
function ProjectFilterMenu({
  options,
  value,
  onChange,
}: {
  readonly options: readonly WorkspaceSummary[]
  readonly value: string | null
  readonly onChange: (value: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const menuId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const activeLabel = value ? options.find((workspace) => workspace.id === value)?.name : null

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (!containerRef.current?.contains(event.relatedTarget as Node | null)) {
      setOpen(false)
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape' && open) {
      event.stopPropagation()
      setOpen(false)
    }
  }

  function choose(next: string | null) {
    onChange(next)
    setOpen(false)
  }

  return (
    <div
      ref={containerRef}
      className="parts-project-menu"
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        className={
          activeLabel
            ? 'parts-project-menu-trigger parts-project-menu-trigger-selected'
            : 'parts-project-menu-trigger'
        }
        aria-label="Filter by project"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {/* UID-225: the leading layers icon + trailing chevron are fg-3 in
            the design, distinct from the trigger's own fg-1 label text (or
            brand-navy for both, once a project is selected — see
            `.parts-project-menu-trigger-selected`'s icon override) — each
            gets its own color via this wrapper rather than inheriting the
            button's `color`. */}
        <span className="parts-project-menu-trigger-icon">
          <IconShipTogether size={14} />
        </span>
        <span>{activeLabel ?? 'All projects'}</span>
        <span className="parts-project-menu-trigger-icon">
          <DownOutlined aria-hidden="true" style={{ fontSize: 13 }} />
        </span>
      </button>
      {open && (
        <div id={menuId} role="menu" className="parts-project-menu-list">
          <button
            type="button"
            role="menuitem"
            className={
              value === null
                ? 'parts-project-menu-item parts-project-menu-item-active'
                : 'parts-project-menu-item'
            }
            onClick={() => choose(null)}
          >
            All projects
          </button>
          {options.map((workspace) => (
            <button
              key={workspace.id}
              type="button"
              role="menuitem"
              className={
                value === workspace.id
                  ? 'parts-project-menu-item parts-project-menu-item-active'
                  : 'parts-project-menu-item'
              }
              onClick={() => choose(workspace.id)}
            >
              {workspace.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/** UID-377 — the `label` override prop went away with the "Projects" tab, which
 * was its only caller ("Loading projects"); both remaining call sites always
 * used the default, so the prop had no callers left.
 *
 * NOT a claim that this label is right everywhere: the BOMs tab's skeleton
 * (the `bomsState.status === 'loading'` branch above) announces "Loading parts
 * library" while `GET /boms` is in flight, which is wrong for that tab. That
 * predates UID-377 — the BOMs call site always took the default — and fixing it
 * was outside this bug's scope. It needs either this prop back with
 * `label="Loading BOMs"`, or a second skeleton for that branch. */
function PartsLibrarySkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading parts library" className="parts-library-skeleton">
      {SKELETON_ROW_KEYS.map((key) => (
        <Skeleton key={key} height={48} />
      ))}
    </div>
  )
}
